import style from "@/styles/pages/Login/Login.module.css";
import { useDispatch, useSelector } from "react-redux";
import clsx from "clsx";
import { useLocation, useSearchParams } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { createApiClient } from "@/lib/Requests";
import { useEffect, useState } from "react";
import {
  clearApplicantState,
  clearJointState,
  setCredentialsCreated,
  setDateOfBirthDay,
  setDateOfBirthMonth,
  setDateOfBirthYear,
  setFirstName,
  setLastName,
  setMaritalStatus,
  setMiddleName,
  setNumOfDependents,
  setPhoneNumber,
  setPhoneType,
  setSin,
  setTitle,
  setApplicantId,
  setIsProspect,
  clearVerificationCounters,
  setEmailAddress,
  setExistingMemberFields
} from "@/slices/applicantSlice";
import {
  clearAddressState,
  setAddressId,
  setCity,
  setCountry,
  setPostalCode,
  setProvince,
  setStreetName,
  setStreetNumber,
  setStreetType,
  setUnitNumber,
  setUnitType
} from "@/slices/homeAddressSlice";
import {
  clearPropertyAddressState
} from "@/slices/propertyAddressSlice";
import { clearMailingAddressState } from "@/slices/mailingAddressSlice";
import {
  clearCredentialState,
  setAccessToken,
  setEmail,
} from "@/slices/credentialSlice";
import { clearProgressState, setLegalComplete } from "@/slices/progressSlice";
import { clearLegalTermsState, setAMLFieldsCompleted, setMeridianOnly, setNatureOfBanking, setOccupation, setOntarioWork, setOwnerOperator, setPolExposed, setPrimaryOtherPurposeGIC, setPrimaryPurpose, setPrimaryPurposeGIC, setResStatus, setTaxNotCanadaNotUS, setTaxResidentorCitizenUs } from "@/slices/legalTermsSlice";
import { RootState } from "@/store/dtm";
import { useForm } from "react-hook-form";
import { UserDto } from "@/lib/DTMWebAPIClient.generated";
import {
  clearPropertyInfoState,
} from "@/slices/propertyInfoSlice";
import {
  clearPreviousAddressState,
} from "@/slices/PreviousAddressSlice";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import {
  clearTwoFactorState,
  setChallenge2FA,
  setChallengeQuestion,
  setSessionId,
} from "@/slices/twoFactorAuthSlice";
import {
  clearCoApplicantState,
} from "@/slices/coApplicantSlice";
import FadeTransition from "@/styles/animation/FadeTransition";
import { useAuth } from "@/context/AuthContext";
import { clearEmploymentState } from "@/slices/employmentSlice";
import Cookies from "js-cookie";
import { AnimatePresence, motion } from "framer-motion";
import { fundmoreSSO, selectFundmoreSSO, setFundmoreSSO } from "@/slices/sessionDataSlice";
import ExistingMemberCard from "@/components/GetStarted/ExistingMemberCard";

export default function SSOLoginDocIQ() {
  const client = createApiClient({ timeout: 60000 });
  const [searchParams, setSearchParams] = useSearchParams();

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { login } = useAuth();
  const { state } = useLocation();

  const [passwordShown, setPasswordShown] = useState(false);
  const [passwordReset, setPasswordReset] = useState(false);
  const [displayError, setDisplayError] = useState(false);
  const [displayLogin, setDisplayLogin] = useState(false);
  const [docIqToken, setDocIqToken] = useState("");
  const [signInText, setSignInText] = useState("Sign in to your account");
  const [displayAttemptsExceeded, setDisplayAttemptsExceeded] = useState(false);
  const [errorMessage, setErrorMessage] = useState(
    "Sorry, we have locked your account because there have been too many attempts. Please call us at 1-866-592-2226 to unlock your account."
  );
  const [loginInProgress, setLoginInProgress] = useState(false);

  const application = useSelector(
    (state: RootState) => state.application.application
  );

  const methods = useForm({
    defaultValues: {
      email: "",
      password: "",
    },
  });

  // Detect browser back button to return from login form to option selection
  useEffect(() => {
    const handlePopState = () => {
      if (displayLogin) {
        setDisplayLogin(false);
        // Prevent navigating away
        window.history.pushState({ internal: true }, document.title);
      }
    };

    // When entering the login view, push a history state so back goes to options
    if (displayLogin) {
      window.history.pushState({ internal: true }, document.title);
    }

    window.addEventListener("popstate", handlePopState);
    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [displayLogin]);

  useEffect(() => {
    document.title = "Meridian Lending - Sign In";
    if (application.applicationErrorCode != "") {
      setErrorMessage(application.applicationErrorMessage);
      setDisplayError(true);
    }
    if (state && state.passwordChanged) {
      setPasswordReset(true);
    } else if (state && state.accountLocked) {
      setErrorMessage("Sorry, we have locked your account because there have been too many failed security code attempts. Please call us at 1-866-592-2226 to unlock your account.");
      setDisplayError(true);
    }
    dispatch(clearCredentialState());
    dispatch(clearApplicantState());
    dispatch(clearEmploymentState());
    dispatch(clearAddressState());
    dispatch(clearMailingAddressState());
    dispatch(clearPreviousAddressState());
    dispatch(clearProgressState());
    dispatch(clearLegalTermsState());
    dispatch(clearJointState());
    dispatch(clearCoApplicantState());
    dispatch(clearPropertyInfoState());
    dispatch(clearPropertyAddressState());
    dispatch(clearTwoFactorState());
    dispatch(clearVerificationCounters());

    let relayState = "";
    try {
      relayState = (searchParams.get("RelayState") != null) ? searchParams.get("RelayState") + "" : "";

    } catch (error) {
      console.log("Error decoding relay state", error);
      navigate("/error/8", { replace: true });
    }

    if (relayState != "") {
      //Convert relay state to xml in backend to parse data
      client.dociqParse(relayState).then((result) => {
        if (result) {
          let ssoResult: fundmoreSSO = {
            issuer: result.issuer,
            nameID: result.nameID,
            email: result.email,
            request: relayState,
            attributes: result.attributes
          };
          dispatch(setFundmoreSSO(ssoResult));
          if (result.email && result.email != "") {
            dispatch(setEmail(result.email));
          }
          setDocIqToken(relayState);
        }
      }).catch((error) => {
        console.log("Error fetching sso data", error);
        navigate("/error/8", { replace: true });
      });
    } else {
      console.log("No relay state found");
      navigate("/error/8", { replace: true });
    }
  }, []);

  const handleClick = (type: number) => {
    //Validate login
    if (type == 1) {
      setDisplayError(false);
      setDisplayAttemptsExceeded(false);
      setLoginInProgress(true);
      let missingFields = false;

      //Ensure fields are not empty
      if (methods.getValues("email").trim() == "") {
        methods.setError("email", {
          type: "input",
          message: "Email or Member Number is required.",
        });
        missingFields = true;
      }
      if (methods.getValues("password").trim() == "") {
        methods.setError("password", {
          type: "input",
          message: "Password is required.",
        });
        missingFields = true;
      }
      if (methods.formState.errors.email || methods.formState.errors.password || missingFields) {
        setLoginInProgress(false);
        return;
      }

      //Check if a token is stored as a cookie
      let savedToken = Cookies.get(methods.getValues("email"));

      //Check if credentials is a member number; if not, use prospect login endpoint
      client
        .apiAuthLogin(
          new UserDto({
            email: methods.getValues("email"),
            password: methods.getValues("password"),
            clientToken: savedToken ? savedToken : undefined,
            docIqToken: docIqToken
          })
        )
        .then(function (loginResult) {
          console.log(loginResult);

          if (loginResult.applicant != undefined) {
            dispatch(setApplicantId(loginResult.applicant.id ? loginResult.applicant.id : 0));
            dispatch(setIsProspect(loginResult.applicant.memberNo == undefined || loginResult.applicant.memberNo == 0));
          }

          //Prospect Login
          if (loginResult.accessToken != undefined && loginResult.auth2FA == undefined) {
            let authenticatedClient = createApiClient({
              timeout: 60000,
              token: loginResult.accessToken,
            });
            authenticatedClient
              .apiApplicationsFetch()
              .then((result) => {
                console.log(result);
                let dealId = 0;
                let applicantId = 0;
                let fName = "";
                let lName = "";
                let email = "";
                let dob = {
                  day: "",
                  month: "",
                  year: ""
                }
                if (result != undefined && result.applicant != undefined) {
                  let applicantInfo = result.applicant;
                  applicantId = applicantInfo.id ? applicantInfo.id : 0;
                  fName = applicantInfo.firstName ? applicantInfo.firstName : "";
                  lName = applicantInfo.lastName ? applicantInfo.lastName : "";
                  email = applicantInfo.email ? applicantInfo.email : "";
                  dob = {
                    day: applicantInfo.dob_day ? applicantInfo.dob_day : "",
                    month: applicantInfo.dob_month ? applicantInfo.dob_month : "",
                    year: applicantInfo.dob_year ? applicantInfo.dob_year : ""
                  }
                }

                if (result != undefined && result.application != undefined) {
                  let applicationInfo = result.application;
                  dealId = applicationInfo.id ? applicationInfo.id : 0;
                }

                if (loginResult.docIqDestination == undefined || loginResult.docIqDestination == "") {
                  navigate("/error/8", { replace: true });
                  setLoginInProgress(false);
                  return;
                } else {
                  window.location.replace(loginResult.docIqDestination);
                }
                
              })
              .catch((error) => {
                console.log("Error saving application info", error);
                setLoginInProgress(false);
              });

            dispatch(setEmail(methods.getValues("email")));
            dispatch(setCredentialsCreated(true));
            login(
              loginResult.accessToken != undefined ? loginResult.accessToken : "",
              loginResult.refreshToken != undefined ? loginResult.refreshToken : ""
            );
            setSignInText("Redirecting to Document Portal....");
          }
          //Existing Member (with Prompt for 2FA or Security Question)
          else if (
            loginResult.accessToken == undefined &&
            loginResult.auth2FA != undefined
          ) {
            dispatch(setEmail(methods.getValues("email")));
            dispatch(setIsProspect(false));
            dispatch(setAccessToken(loginResult.accessToken != undefined ? loginResult.accessToken : ""));
            dispatch(setChallenge2FA(loginResult.auth2FA?.challenge2FA));
            dispatch(setChallengeQuestion(loginResult.auth2FA?.challengeQuestion));
            dispatch(setSessionId(loginResult.auth2FA?.sessionId));

            if (
              loginResult.auth2FA != undefined &&
              loginResult.auth2FA.challengeQuestion != undefined
            ) {
              navigate("/security");
            } else if (
              loginResult.auth2FA != undefined &&
              loginResult.auth2FA.challenge2FA != undefined
            ) {
              navigate("/two-fa-security");
            }
          }
          //Existing Member (with 'Remember Me' Option Selected from Previous Session)
          else if (loginResult.accessToken != undefined && loginResult.auth2FA != undefined) {
            dispatch(setCredentialsCreated(true));
            login(loginResult.accessToken != undefined ? loginResult.accessToken : "", loginResult.refreshToken != undefined ? loginResult.refreshToken : "");
            if (loginResult.applicant != undefined) {
              let applicantInfo = loginResult.applicant;

              let dobMonth = applicantInfo.dateOfBirth.month() + 1;
              let dobDay = applicantInfo.dateOfBirth.date();

              dispatch(setApplicantId(applicantInfo.id ? applicantInfo.id : 0));
              dispatch(setTitle(applicantInfo.title ? applicantInfo.title : ""));
              dispatch(setFirstName(applicantInfo.firstName));
              dispatch(setMiddleName(applicantInfo.middleName ? applicantInfo.middleName : ""));
              dispatch(setLastName(applicantInfo.lastName));
              dispatch(setPhoneNumber(applicantInfo.phoneNumber));
              dispatch(setPhoneType(applicantInfo.phoneType ? applicantInfo.phoneType.replace(/\w/, p => p.toUpperCase()) : ""));
              dispatch(setEmailAddress(applicantInfo.email));
              dispatch(setDateOfBirthYear(applicantInfo.dateOfBirth.year() + ""));
              dispatch(setDateOfBirthMonth(dobMonth.toString().length == 1 ?
                "0" + dobMonth.toString() : dobMonth.toString()));
              dispatch(setDateOfBirthDay(dobDay + ""));
              dispatch(setMaritalStatus(applicantInfo.maritalStatus ? applicantInfo.maritalStatus : ""));
              dispatch(setNumOfDependents(applicantInfo.numberOfDependents ? applicantInfo.numberOfDependents : 0));

              if (applicantInfo.address) {
                dispatch(setAddressId(applicantInfo.address[0].id ? applicantInfo.address[0].id : 0));
                dispatch(setStreetNumber(applicantInfo.address[0].streetNumber ? applicantInfo.address[0].streetNumber : ""));
                dispatch(setStreetName(applicantInfo.address[0].streetName ? applicantInfo.address[0].streetName : ""));
                dispatch(setStreetType(applicantInfo.address[0].streetType ? applicantInfo.address[0].streetType : ""));
                dispatch(setUnitType(applicantInfo.address[0].unitType ? applicantInfo.address[0].unitType : ""));
                dispatch(setUnitNumber(applicantInfo.address[0].unitNumber ? applicantInfo.address[0].unitNumber : ""));
                dispatch(setCity(applicantInfo.address[0].city ? applicantInfo.address[0].city : ""));
                dispatch(setProvince(applicantInfo.address[0].region ? applicantInfo.address[0].region : ""));
                dispatch(setPostalCode(applicantInfo.address[0].postalCode ? applicantInfo.address[0].postalCode : ""));
                dispatch(setCountry(applicantInfo.address[0].country ? applicantInfo.address[0].country : ""));

                //Attempt to parse the street type based on whether it contains multiple words
                if ((applicantInfo.address[0].streetType == undefined || applicantInfo.address[0].streetType == "") && applicantInfo.address[0].streetName && applicantInfo.address[0].streetName.split(" ").length > 1) {
                  let streetNameComponents = applicantInfo.address[0].streetName.split(" ");
                  let newStreetName = "";
                  for (let i = 0; i < streetNameComponents.length - 1; i++) {
                    newStreetName += streetNameComponents[i] + " ";
                  }
                  let streetType = streetNameComponents[streetNameComponents.length - 1].toUpperCase();
                  dispatch(setStreetType(streetType));
                  dispatch(setStreetName(newStreetName.trim()));
                } else if (applicantInfo.address[0].streetType != undefined && applicantInfo.address[0].streetType != "") {
                  dispatch(setStreetType(applicantInfo.address[0].streetType));
                }
              }

              //Check Validity of Ovation Fields
              let phoneNumberReg = new RegExp(/^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}$/);
              dispatch(setExistingMemberFields({
                isFirstNameAvailable: applicantInfo.firstName.trim() != '',
                isLastNameAvailable: applicantInfo.lastName.trim() != '',
                isPhoneNumberAvailable: phoneNumberReg.test(applicantInfo.phoneNumber),
                isSinAvailable: true,
                isProvinceAvailable: applicantInfo.address != undefined && applicantInfo.address[0].region ? applicantInfo.address[0].region.trim() != '' : false,
                isPostalCodeAvailable: applicantInfo.address != undefined && applicantInfo.address[0].postalCode ? applicantInfo.address[0].postalCode.trim() != '' : false,
                isCountryAvailable: applicantInfo.address != undefined && applicantInfo.address[0].country.trim() != ''
              }));
            }

            let authenticatedClient = createApiClient({ timeout: 60000, token: loginResult.accessToken });
            authenticatedClient.apiApplicationsFetch().then(async (appData) => {
              if (appData) {

                //Navigate to DOC IQ

                //Prefil member info such as subject property, address, etc.
                //processUserAppData(appData, dispatch);

                //TODO: If member attempts to sign in with a different invitiation code; redirect to login screen and display error message
                //navigate("/home", { replace: true });

              } else {
                navigate("/confirm-consent", { replace: true });
              }
            }).catch((error) => {
              console.log("Error fetching application info", error);
            });
          }
        })
        .catch((error) => {
          if (error && error.response) {
            let errorMessage = "";
            if (error.response && error.response.code == "LB10046001") {
              errorMessage = "Sorry, we are having trouble signing you in. You may have entered an incorrect Member Number and/or Password. "
                + "To prevent being locked out, select continue to sign in through online banking or reset your password";
              setDisplayAttemptsExceeded(true);
            } else if (error.response && error.response.message) {
              errorMessage = error.response.message;
              setDisplayError(true);
            } else {
              errorMessage = "An unexpected error occurred; please try again later.";
              setDisplayError(true);
            }
            setErrorMessage(errorMessage);
          }
          setLoginInProgress(false);
          if (methods.formState.errors.email || methods.formState.errors.password) {
            return;
          }
        });
      return;
    } else if (type == 2) {
      navigate("/forgot-password");
    } else if (type == 3) {
      setDisplayLogin(false);
    }
  };

  const togglePasswordVisiblity = () => {
    setPasswordShown(passwordShown ? false : true);
  };

  const nextStep = (type: number) => {
    if (type == 1) {
      setDisplayLogin(true);
    } else {
      navigate("/single/accept-terms");
    }

  }

  return (
    <FadeTransition>
      <main aria-labelledby="main-content">
        <AnimatePresence>
          <div className="main-container max-md:mt-10">
            {displayLogin ?
              <motion.div
                key={0}
                initial={{ x: 100, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                <div className={clsx(style.login, "container", "mt-10")}>
                  <div className={style.emHeading}>
                    <div className="grid grid-cols-1">
                      <h1>Sign in to your account</h1>
                      <p>If you are currently a Meridian Member,</p>
                      <p>sign in using your Member number.</p>
                    </div>
                  </div>
                  {passwordReset && (
                    <div
                      className={clsx(
                        "flex flex-row pcaautocomplete mt-5",
                        style.successContainer
                      )}
                    >
                      <img src="assets/success-info-icon.svg" alt="error" />
                      <span className="text-sm ml-2 mt-1">
                        Your password was successfully updated. Please sign in to
                        continue.
                      </span>
                    </div>
                  )}
                  {displayError && (
                    <div
                      className={clsx(
                        "flex flex-row pcaautocomplete mt-5",
                        style.errorContainer
                      )}
                    >
                      <img src="assets/error-info-icon.svg" alt="error" />
                      <span className="text-sm ml-2 mt-1">{errorMessage}</span>
                    </div>
                  )}
                  {displayAttemptsExceeded && (
                    <div
                      className={clsx(
                        "flex flex-row pcaautocomplete mt-5",
                        style.errorContainer
                      )}
                    >
                      <img src="assets/error-info-icon.svg" alt="error" />
                      <span className="text-sm ml-2 mt-1">Sorry, we are having trouble signing you in. You may have entered an incorrect Member Number and/or Password.
                        To prevent being locked out, select <a className="brand-blue-text cursor-pointer" href="https://banking.meridiancu.ca/Security_Login"
                          style={{ textDecorationLine: "underline" }}>continue</a> to sign in through online banking or reset your password.</span>
                    </div>
                  )}
                  <div className="mt-5">
                    <label htmlFor="email">Email Address or Member number</label>
                    <div className="flex relative">
                      <span>
                        <img
                          src="assets/error-icon.svg"
                          className={clsx(
                            methods.formState?.errors?.email ? "" : "hidden",
                            "w-4 h-4 absolute top-6 max-xxs:ml-[210px] max-sm:ml-[300px] xs:max-md:ml-[370px] md:ml-[430px]"
                          )}
                          alt="error"
                          height={5}
                          width={5}
                        />
                      </span>
                      <input
                        className={clsx(
                          methods.formState?.errors?.email?.message
                            ? "error"
                            : "",
                          "mt-2"
                        )}
                        type="email"
                        id="email"
                        autoComplete="email"
                        {...methods.register("email", {
                          required: "Enter your email address",
                        })}
                        onChange={(e) => {
                          if (e.target.value.trim() != "") {
                            methods.setValue("email", e.target.value);
                            methods.clearErrors("email");
                          }
                        }}
                      />
                    </div>
                  </div>
                  {methods.formState?.errors?.email?.message && (
                    <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                      {methods.formState.errors.email.message}
                    </span>
                  )}
                  <div className="mt-3 pass-wrapper">
                    <label htmlFor="password">Password</label>
                    <div className="flex relative">
                      <span className={clsx(passwordShown ? "hidden" : " ")}>
                        <img
                          onClick={togglePasswordVisiblity}
                          src="assets/eye-icon.svg"
                          className={clsx(
                            "w-4 h-4 absolute top-6 max-xxs:ml-[210px] max-sm:ml-[300px] xs:max-md:ml-[370px] md:ml-[430px]"
                          )}
                          alt="error"
                          height={5}
                          width={5}
                        />
                      </span>
                      <span className={clsx(passwordShown ? " " : "hidden")}>
                        <img
                          onClick={togglePasswordVisiblity}
                          src="assets/eye-slash-icon.svg"
                          className={clsx(
                            "w-4 h-4 absolute top-6 max-xxs:ml-[210px] max-sm:ml-[300px] xs:max-md:ml-[370px] md:ml-[430px]"
                          )}
                          alt="error"
                          height={5}
                          width={5}
                        />
                      </span>
                      <input
                        className={clsx(
                          methods.formState?.errors?.password?.message
                            ? "error"
                            : "",
                          "mt-2"
                        )}
                        type={passwordShown ? "text" : "password"}
                        id="password"
                        autoComplete="current-password"
                        {...methods.register("password", {
                          required: "Enter your password",
                        })}
                        onKeyDown={(event) => {
                          if (event.key === "Enter") {
                            handleClick(1);
                          }
                        }}
                        onChange={(e) => {
                          if (e.target.value.trim() != "") {
                            methods.setValue("password", e.target.value);
                            methods.clearErrors("password");
                          }
                        }}
                      />
                    </div>
                  </div>
                  {methods.formState?.errors?.password?.message && (
                    <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                      {methods.formState.errors.password.message}
                    </span>
                  )}
                  {/* <div className="mt-3">
              <a onClick={() => { handleClick(2) }}>
                <p className="active underline cursor-pointer">Forgot your password?</p>
              </a>
            </div> */}
                  <div className="mt-3">
                    <p><a className="active underline cursor-pointer" onClick={() => { handleClick(3) }}>Click here</a> to sign up for an account</p>
                  </div>
                  <div className="flex relative">
                    <button
                      onClick={() => {
                        handleClick(1);
                      }}
                      disabled={loginInProgress}
                      className={clsx(
                        "primary mt-5 w-full sm:w-28 h-11 px-6",
                        loginInProgress ? style.loginDisabled : ""
                      )}
                    >
                      {loginInProgress ? (
                        <motion.div
                          initial={{ opacity: 0, scale: 0 }}
                          animate={{ opacity: 1, scale: 1 }}
                          exit={{ opacity: 0, scale: 0 }}>
                          <FontAwesomeIcon
                            icon={faSpinner}
                            spin
                            style={{ marginRight: 8 }}
                            className={clsx(loginInProgress ? "" : "hidden")}
                          />
                        </motion.div>
                      ) : null}
                      {signInText}
                    </button>
                  </div>
                  <div className="text-center mt-5">
                    <p className="mt-5 mb-3">
                      If you're having difficulty signing in, please call
                    </p>
                    <p className="active">1-866-592-2226</p>
                  </div>
                </div>
                <div className="text-center">
                  <p className="mt-3 mb-3">
                    This site is protected by reCAPTCHA and the{" "}
                  </p>
                  <p className="">
                    Google{" "}
                    <a
                      className="active"
                      href="https://policies.google.com/privacy"
                      target="_blank"
                    >
                      Privacy Statement
                    </a>{" "}
                    and{" "}
                    <a
                      className="active"
                      href="https://policies.google.com/terms"
                      target="_blank"
                    >
                      Terms of Service
                    </a>{" "}
                    apply.
                  </p>
                </div>
              </motion.div>
              :
              <>
                <motion.div
                  key={1}
                  initial={{ x: -100, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className={clsx(style.ssoOptions, "mt-5",)}
                >
                  <div>
                    <div className="grid grid-cols-1" style={{ textAlign: "center" }}>
                      <h1>Are you a Meridian Member or have you</h1>
                      <h1>started an online application with us?</h1>
                    </div>
                  </div>
                  <div className="content-container">
                    <div className="grid grid-cols-1 md:grid-cols-2">
                      <div className="mb-5 sm:w-full">
                        <div onClick={() => nextStep(1)}>
                          <ExistingMemberCard
                            cardHeight={200}
                            cardWidth={255}
                            imageSrc="assets/rob-log-in.svg"
                            cardText="Yes, sign me in"
                            secondaryText="Continue as a member or resume your online application"
                          />
                        </div>
                      </div>
                      <div>
                        <div onClick={() => nextStep(2)}>
                          <ExistingMemberCard
                            cardHeight={200}
                            cardWidth={255}
                            imageSrc="assets/create-account.svg"
                            cardText="No, sign me up"
                            secondaryText="Create your digital profile"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              </>}
          </div>
        </AnimatePresence>
      </main>
    </FadeTransition>
  );
}
